import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { propertyAPI } from '../../services/api';
import './VerifyPropertyPage.css';

export default function VerifyPropertyPage() {
  const { propertyUid, tenantSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!propertyUid) {
      setLoading(false);
      setError('मालमत्ता ओळख क्रमांक (Property UID) सापडला नाही.');
      return;
    }

    const fetchVerification = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await propertyAPI.publicVerify(propertyUid);
        setData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('सदर मालमत्ता नोंद डिजिटल नोंदवहीत सापडली नाही. कृपया बारकोड/QR कोड तपासा.');
        } else {
          setError('नोंद पडताळणी करण्यात अडचण आली. कृपया काही वेळाने पुन्हा प्रयत्न करा.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVerification();
  }, [propertyUid]);

  if (loading) {
    return (
      <div className="verify-property-page">
        <div className="verify-loading">
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⏳</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>अधिकृत मालमत्ता नोंद पडताळली जात आहे...</div>
          <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: 4 }}>Verifying Property Record...</p>
        </div>
      </div>
    );
  }

  if (error || !data || !data.verified) {
    return (
      <div className="verify-property-page">
        <div className="verify-error-box">
          <div className="verify-error-icon">❌</div>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.25rem' }}>अवैध नोंद किंवा त्रुटी</h2>
          <p style={{ margin: 0, fontSize: '0.92rem' }}>{error || 'सदर मालमत्ता नोंद उपलब्ध नाही.'}</p>
          <div style={{ marginTop: 24 }}>
            <Link to="/" style={{ color: '#1d4ed8', fontSize: '0.9rem', fontWeight: 600 }}>
              ← मुख्य पृष्ठावर जा
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { property, tenant, owners, valuation, verified_at } = data;
  const ownerNames = owners && owners.length ? owners.map(o => o.name).join(', ') : 'नोंद उपलब्ध नाही';
  const downloadUrl = propertyAPI.publicDownloadNamuna8Url(property.property_uid);
  const verifiedDateStr = verified_at
    ? new Date(verified_at).toLocaleDateString('mr-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="verify-property-page">
      {/* Header */}
      <div className="verify-property-header">
        <div className="verify-property-emblem">🏛️</div>
        <h1 className="verify-property-title">मालमत्ता नोंदणी व कर आकारणी पडताळणी</h1>
        <p className="verify-property-subtitle">Official Property Assessment Register Verification (नमुना ८)</p>
      </div>

      {/* Main Card */}
      <div className="verify-card">
        {/* Verification Status Banner */}
        <div className="verify-status-banner valid">
          <div className="verify-status-icon">✓</div>
          <div className="verify-status-text">अधिकृत व सत्यापित नोंद (Official Verified Record)</div>
          <div className="verify-status-subtext">ग्रामपंचायत डिजिटल पोर्टलवर सदर मालमत्ता नोंद अधिकृतपणे वैध आहे.</div>
        </div>

        {/* Card Body */}
        <div className="verify-body">
          {/* GP Jurisdiction Info */}
          <div className="verify-gp-header">
            <div className="verify-gp-name">ग्रामपंचायत {tenant?.name || 'देऊळगाव गाडा'}</div>
            <div className="verify-gp-jurisdiction">
              {[
                tenant?.taluka && `तालुका: ${tenant.taluka}`,
                tenant?.district && `जिल्हा: ${tenant.district}`,
                'महाराष्ट्र राज्य',
              ].filter(Boolean).join(' | ')}
            </div>
          </div>

          {/* Details Grid */}
          <div className="verify-grid">
            <div className="verify-cell">
              <div className="verify-cell-label">मालमत्ता क्रमांक (Assessment No.)</div>
              <div className="verify-cell-value highlight">{property.assessment_number || property.property_number || '—'}</div>
            </div>

            <div className="verify-cell">
              <div className="verify-cell-label">वार्ड / प्रभाग क्रमांक</div>
              <div className="verify-cell-value">{property.ward_number ? `वार्ड क्र. ${property.ward_number}` : '—'}</div>
            </div>

            <div className="verify-cell full-width">
              <div className="verify-cell-label">मालकाचे नाव (Owner / Liable Person)</div>
              <div className="verify-cell-value">{ownerNames}</div>
            </div>

            <div className="verify-cell full-width">
              <div className="verify-cell-label">पत्ता / रस्त्याचे नाव</div>
              <div className="verify-cell-value">{property.address || property.locality || '—'}</div>
            </div>

            <div className="verify-cell">
              <div className="verify-cell-label">गट / भूमापन क्रमांक</div>
              <div className="verify-cell-value">{property.gat_number || property.survey_number || '—'}</div>
            </div>

            <div className="verify-cell">
              <div className="verify-cell-label">बांधकाम वर्ष</div>
              <div className="verify-cell-value">{property.construction_year || '—'}</div>
            </div>

            <div className="verify-cell">
              <div className="verify-cell-label">बांधकाम प्रकार</div>
              <div className="verify-cell-value">{property.construction_type || '—'}</div>
            </div>

            <div className="verify-cell">
              <div className="verify-cell-label">क्षेत्रफळ (चौ.फू / चौ.मी)</div>
              <div className="verify-cell-value">
                {property.built_up_area_sqft ? `${property.built_up_area_sqft} चौ.फू (${(property.built_up_area_sqft * 0.092903).toFixed(2)} चौ.मी)` : '—'}
              </div>
            </div>

            {valuation && (
              <div className="verify-cell full-width">
                <div className="verify-cell-label">भांडवली आकारणी मूल्य (Capital Valuation)</div>
                <div className="verify-cell-value">₹ {Number(valuation.capital_value || valuation.assessed_value || 0).toLocaleString('en-IN')}</div>
              </div>
            )}
          </div>

          {/* Action Box: Direct PDF Download */}
          <div className="verify-action-box">
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="verify-download-btn"
            >
              📄 अधिकृत नमुना ८ उतारा (PDF) पहा / डाउनलोड करा
            </a>
            <p className="verify-notice">
              सदर उतारा हा मालकी हक्काचा नसून करआकारणीचा आहे. हा उतारा डिजिटल प्रणालीद्वारे {verifiedDateStr} रोजी सत्यापित केला गेला आहे.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Footer */}
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <Link to={tenant?.slug ? `/gp/${tenant.slug}` : '/'} style={{ color: '#1d4ed8', fontSize: '0.88rem', textDecoration: 'none', fontWeight: 600 }}>
          ← ग्रामपंचायत पोर्टल मुख्य पृष्ठ
        </Link>
      </div>
    </div>
  );
}
